/**
 * Topbar - Top navigation bar component
 * Provides search and user menu
 */

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Settings, LogOut } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Input } from '@/components/ui/Input';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

export function Topbar(): React.ReactElement {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  // Get user initials for avatar (prefer firstName/lastName over legacy name)
  const getUserInitials = (): string => {
    if (!user) return '?';
    // Prefer firstName/lastName if available
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.firstName) {
      return user.firstName.substring(0, 2).toUpperCase();
    }
    // Fallback to legacy name field
    if (user.name) {
      const nameParts = user.name.split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0]?.[0] ?? ''}${nameParts[1]?.[0] ?? ''}`.toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  // Get display name (prefer full name from firstName/lastName)
  const getDisplayName = (): string => {
    if (!user) return 'User';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) {
      return user.firstName;
    }
    return user.name || user.email || 'User';
  };

  return (
    <div className="flex h-full items-center justify-between border-b border-border-subtle bg-surface px-8">
      {/* Search input */}
      <div className="w-96">
        <Input
          variant="search"
          type="search"
          placeholder="Search projects, creatives..."
          aria-label="Search"
        />
      </div>

      {/* User menu */}
      <div className="flex items-center gap-4">
        <ThemeToggle />
        <DropdownMenu.Root open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
          <DropdownMenu.Trigger asChild>
            <button
              className="flex items-center gap-3 rounded-pill py-2 pl-2 pr-4 transition-all duration-base hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/16"
              aria-label="User menu"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand">
                <span className="font-semibold text-xs text-inverse">
                  {getUserInitials()}
                </span>
              </div>
              <span className="font-medium text-sm text-primary">
                {getDisplayName()}
              </span>
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="z-50 min-w-56 rounded-lg border border-border-subtle bg-surface p-2 shadow-md"
              sideOffset={8}
              align="end"
            >
              {/* User info section */}
              <div className="border-b border-border-subtle px-3 py-3 mb-2">
                <p className="font-medium text-sm text-primary">
                  {getDisplayName()}
                </p>
                <p className="text-xs text-muted">{user?.email}</p>
              </div>

              {/* Menu items */}
              <DropdownMenu.Item asChild>
                <button
                  onClick={() => {
                    navigate('/settings/profile');
                    setIsUserMenuOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-secondary transition-all duration-base',
                    'hover:bg-surface-subtle focus-visible:outline-none focus-visible:bg-surface-subtle'
                  )}
                >
                  <Settings className="h-4 w-4" aria-hidden="true" />
                  <span>Settings</span>
                </button>
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="my-2 h-px bg-border-subtle" />

              <DropdownMenu.Item asChild>
                <button
                  onClick={handleSignOut}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-danger transition-all duration-base',
                    'hover:bg-danger-soft focus-visible:outline-none focus-visible:bg-danger-soft'
                  )}
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  <span>Sign out</span>
                </button>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </div>
  );
}
